/**
 * Segmented Control Component - The Intelligent Standard
 *
 * A refined toggle control for switching between options.
 */

import { ReactNode } from 'react';

export interface SegmentedControlOption<T> {
  value: T;
  label: string;
  icon?: ReactNode;
}

export interface SegmentedControlProps<T> {
  options: SegmentedControlOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: 'sm' | 'md';
  fullWidth?: boolean;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  size = 'md',
  fullWidth = false,
}: SegmentedControlProps<T>) {
  const sizeStyles = {
    sm: 'text-caption px-3 py-1.5',
    md: 'text-body-sm px-4 py-2',
  };

  return (
    <div
      className={`
        inline-flex bg-cream rounded-lg p-1 gap-1
        ${fullWidth ? 'w-full' : ''}
      `}
    >
      {options.map(option => {
        const isActive = option.value === value;

        return (
          <button
            key={option.value}
            onClick={() => onChange(option.value)}
            className={`
              ${sizeStyles[size]}
              ${fullWidth ? 'flex-1' : ''}
              rounded-md font-medium
              transition-all duration-200
              flex items-center justify-center gap-2
              border-none cursor-pointer
              ${
                isActive
                  ? 'bg-white text-ink shadow-sm'
                  : 'bg-transparent text-slate hover:text-ink'
              }
            `}
          >
            {option.icon}
            <span>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Tab-style segmented control for navigation
 */
export interface TabControlOption<T> {
  value: T;
  label: string;
  count?: number;
}

export interface TabControlProps<T> {
  options: TabControlOption<T>[];
  value: T;
  onChange: (value: T) => void;
}

export function TabControl<T extends string>({
  options,
  value,
  onChange,
}: TabControlProps<T>) {
  return (
    <div className="flex gap-1 border-b border-cream-dark">
      {options.map(option => {
        const isActive = option.value === value;

        return (
          <button
            key={option.value}
            onClick={() => onChange(option.value)}
            className={`
              px-4 py-3
              text-body-sm font-medium
              border-none cursor-pointer
              transition-all duration-200
              relative
              bg-transparent
              ${isActive ? 'text-meridian' : 'text-slate hover:text-ink'}
            `}
          >
            <span className="flex items-center gap-2">
              {option.label}
              {option.count !== undefined && (
                <span
                  className={`
                  px-1.5 py-0.5 rounded-full text-caption
                  ${
                    isActive
                      ? 'bg-meridian/10 text-meridian'
                      : 'bg-cream text-slate'
                  }
                `}
                >
                  {option.count}
                </span>
              )}
            </span>
            {isActive && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-meridian rounded-t" />
            )}
          </button>
        );
      })}
    </div>
  );
}
